import { LayoutSearchParams } from "@/shared/types/layout";
import { Icon, IconProps, Styles } from "@/shared/coralreef";
import { Icon as SvgIcon, AppIcons } from "../icon/Icon";
import { useMatchRoute } from "@tanstack/react-router";
import { important, percent, px } from "csx";
import { Box, Content, Horizontal, Stretch, Vertical } from "gls";
import { FC } from "react";
import { usePermissions } from "shared/hooks/usePermissions";
import { style } from "typestyle";
import { Badge } from "../badge/Badge";
import { useShellHost } from "@/shell-handle/ShellHostProvider";
import { toQueryString, withSelectors } from "@/shell-handle/urlSync";

export const SidebarItem: FC<{
  isCollapsed?: boolean;
  hideTextWhenCollapsed?: boolean;
  icon?: IconProps["icon"];
  svgIcon?: AppIcons;
  text: string;
  collapsedText?: string;
  alertCount?: number;
  navigateTo?: string;
  // Highlight on any route under this path instead of only on `navigateTo`.
  // Section entries point at one landing route (Equipment -> .../pens) but must
  // stay highlighted while the user moves between that section's sibling tabs.
  matchTo?: string;
  navigateParams?: Record<string, number>;
  navigateSearch?: (search: LayoutSearchParams) => LayoutSearchParams;
  onClick?: () => void;
  permissionName?: string;
  "data-testid"?: string;
}> = ({
  isCollapsed,
  hideTextWhenCollapsed,
  icon,
  svgIcon,
  text,
  collapsedText,
  alertCount,
  navigateTo,
  matchTo,
  navigateParams,
  navigateSearch,
  onClick,
  permissionName,
  "data-testid": dataTestId,
}) => {
  const isHighlighted =
    useMatchRoute()({
      to: matchTo ?? navigateTo,
      fuzzy: matchTo !== undefined,
      params: navigateParams
        ? { dashboardId: String(navigateParams?.dashboardId) }
        : undefined,
    }) && navigateTo;

  const host = useShellHost();

  if (permissionName != null && permissionName != undefined) {
    const { hasPermission } = usePermissions();

    if (!hasPermission(permissionName)) {
      return <></>;
    }
  }

  return (
    <Horizontal
      spacing={10}
      horizontalAlign={isCollapsed ? "center" : "left"}
      verticalAlign="center"
      className={style({
        cursor: navigateTo ? "pointer" : "default",
        color: isHighlighted ? Styles.Colours.primary : Styles.Colours.white,
        overflow: "hidden",
      })}
      padding={[0, 0, px(14), isCollapsed ? 0 : px(20)]}
      width={percent(100)}
      data-testid={dataTestId}
      onClick={() => {
        onClick && onClick();

        if (!navigateTo) return;
        let search: Record<string, unknown> | undefined;
        if (typeof navigateSearch === "function") {
          try {
            search = (navigateSearch as unknown as (s: object) => object)(
              {},
            ) as Record<string, unknown>;
          } catch {
            /* ignore */
          }
        }
        let url = withSelectors(navigateTo, host.handle);
        if (search) {
          const qs = new URLSearchParams();
          for (const [k, v] of Object.entries(search)) {
            if (v === undefined || v === null) continue;
            qs.set(k, typeof v === "string" ? v : JSON.stringify(v));
          }
          const s = toQueryString(qs);
          if (s) url += (url.includes("?") ? "&" : "?") + s;
        }
        if (window.location.pathname + window.location.search === url) return;
        window.history.pushState({}, "", url);
        window.dispatchEvent(
          new PopStateEvent("popstate", { state: window.history.state }),
        );
      }}
    >
      <Vertical
        spacing={px(5)}
        sizing={isCollapsed ? "stretch" : "content"}
        horizontalAlign="center"
        width={isCollapsed ? percent(100) : "auto"}
      >
        {(icon || svgIcon) && (
          <Content className={iconContainer}>
            {svgIcon ? (
              <SvgIcon
                icon={svgIcon}
                className={style({ width: important(px(20)) })}
              />
            ) : (
              icon && (
                <Icon
                  icon={icon}
                  className={style({ width: important(px(20)) })}
                />
              )
            )}
            {(alertCount || 0) > 0 && isCollapsed && (
              <Box className={alertDot} />
            )}
          </Content>
        )}
        {isCollapsed && !hideTextWhenCollapsed && (
          <Stretch
            horizontalAlign="center"
            padding={[0, px(5)]}
            width={percent(100)}
            className={style({ boxSizing: "border-box" })}
          >
            <span className={collapsedTextContainer}>
              {collapsedText || text}
            </span>
          </Stretch>
        )}
      </Vertical>
      {!isCollapsed && (
        <Horizontal
          sizing="stretch"
          height={percent(100)}
          spacing={0}
          className={style({ overflow: "hidden" })}
        >
          <span
            className={style({
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            })}
          >
            {text}
          </span>
        </Horizontal>
      )}

      {!isCollapsed && alertCount && <Badge>{alertCount}</Badge>}
    </Horizontal>
  );
};

const collapsedTextContainer = style({
  fontSize: px(10),
  textAlign: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: "normal",
});

const iconContainer = style({ position: "relative" });

const alertDot = style({
  position: "absolute",
  width: px(8),
  height: px(8),
  background: Styles.Colours.red,
  borderRadius: percent(50),
  border: `3px solid ${Styles.Colours.blue}`,
  top: px(-5),
  right: px(-5),
});
